/* eslint-disable max-lines */
/**
 * Utility functions for generating JSON-LD schema markup
 */

import { CityPageDishFragment, CityPageShefFragment } from 'utils/gqlReactTypings.generated.d';

interface OrganizationSchema {
  name: string;
  url: string;
  logo?: string;
  sameAs?: string[];
  contactPoint?: {
    telephone?: string;
    contactType?: string;
    areaServed?: string;
  };
}

interface LocalBusinessSchema {
  name: string;
  description: string;
  url: string;
  image?: string;
  address?: {
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  geo?: {
    latitude?: number;
    longitude?: number;
  };
  areaServed?: string | string[];
  priceRange?: string;
}

/**
 * Generate Organization schema
 */
export const generateOrganizationSchema = (data: Partial<OrganizationSchema>) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: data.name || 'Shef',
  url: data.url || 'https://shef.com',
  logo: data.logo,
  sameAs: data.sameAs || [
    'https://www.facebook.com/ordershef',
    'https://www.instagram.com/ordershef',
    'https://twitter.com/ordershef',
    'https://www.linkedin.com/company/ordershef',
  ],
  contactPoint: data.contactPoint,
});

/**
 * Generate LocalBusiness schema for delivery areas
 */
export const generateLocalBusinessSchema = (data: Partial<LocalBusinessSchema>) => ({
  '@context': 'https://schema.org',
  '@type': 'FoodService',
  '@id': data.url,
  name: data.name || 'Shef - Homemade Food Delivery',
  description: data.description || 'Authentic homemade meals delivered from local home chefs',
  url: data.url,
  image: data.image,
  address: data.address
    ? {
        '@type': 'PostalAddress',
        addressLocality: data.address.addressLocality,
        addressRegion: data.address.addressRegion,
        postalCode: data.address.postalCode,
        addressCountry: data.address.addressCountry || 'US',
      }
    : undefined,
  geo: data.geo
    ? {
        '@type': 'GeoCoordinates',
        latitude: data.geo.latitude,
        longitude: data.geo.longitude,
      }
    : undefined,
  areaServed: data.areaServed,
  priceRange: data.priceRange || '$$',
  servesCuisine: 'Multiple',
  hasDeliveryMethod: {
    '@type': 'DeliveryMethod',
    name: 'OnSitePickup',
  },
});

/**
 * Generate Product schema for dishes
 */
export const generateDishSchema = (dish: CityPageDishFragment) => {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: dish.name,
    description: `${dish.name} by Shef ${dish.shef?.publicName}`,
    image: dish.imageUrl,
    offers: {
      '@type': 'Offer',
      price: dish.price,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Person',
        name: `Shef ${dish.shef?.publicName}`,
      },
    },
  };

  if (dish.averageRating && dish.ratingCount) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: dish.averageRating,
      reviewCount: dish.ratingCount,
      bestRating: 100,
      worstRating: 0,
    };
  }

  return schema;
};

/**
 * Generate Person schema for chefs
 */
export const generateChefSchema = (chef: CityPageShefFragment) => {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: `Shef ${chef.publicName}`,
    image: chef.imageUrl,
    description: chef.bio,
    jobTitle: 'Home Chef',
    worksFor: {
      '@type': 'Organization',
      name: 'Shef',
    },
  };

  if (chef.rating?.score && chef.rating?.count) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: chef.rating.score,
      reviewCount: chef.rating.count,
      bestRating: 5,
      worstRating: 1,
    };
  }

  if (chef.formattedCuisineCategories) {
    schema.knowsAbout = chef.formattedCuisineCategories;
  }

  return schema;
};

/**
 * Generate Service schema for delivery service
 */
export const generateServiceSchema = (cityName?: string, stateName?: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Homemade Food Delivery',
  description: `Authentic homemade meal delivery service${
    cityName ? ` in ${cityName}${stateName ? `, ${stateName}` : ''}` : ''
  }`,
  provider: {
    '@type': 'Organization',
    name: 'Shef',
  },
  areaServed: cityName
    ? {
        '@type': 'City',
        name: cityName,
        containedInPlace: {
          '@type': 'State',
          name: stateName,
        },
      }
    : 'United States',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Homemade Meals',
    itemListElement: [
      {
        '@type': 'OfferCatalog',
        name: 'Cuisines',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Indian Cuisine' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Mexican Cuisine' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Chinese Cuisine' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Italian Cuisine' },
          },
        ],
      },
    ],
  },
});

/**
 * Generate FAQ schema
 */
export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

/**
 * Generate WebSite schema with search action
 */
export const generateWebSiteSchema = (siteUrl: string, searchUrl?: string) => {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Shef',
    url: siteUrl,
  };

  if (searchUrl) {
    schema.potentialAction = {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${searchUrl}?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    };
  }

  return schema;
};

export const generateBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@id': crumb.url,
      name: crumb.name,
    },
  })),
});

export const generateLocationListSchema = (locations: any[], type: 'city' | 'metro' | 'zip', baseUrl: string) => {
  const typeMap = {
    city: { name: 'Cities', schemaType: 'City' },
    metro: { name: 'Metro Areas', schemaType: 'AdministrativeArea' },
    zip: { name: 'ZIP Codes', schemaType: 'PostalAddress' },
  };

  const { name, schemaType } = typeMap[type];

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${name} Where Shef Delivers`,
    description: `Complete list of ${
      type === 'city' ? 'cities' : type === 'metro' ? 'metropolitan areas' : 'ZIP codes'
    } where Shef delivers homemade food`,
    numberOfItems: locations.length,
    itemListElement: locations.slice(0, 50).map((location, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item:
        type === 'zip'
          ? {
              '@type': schemaType,
              postalCode: location.zipCode,
              url: `${baseUrl}/zip/${location.zipCode}`,
            }
          : {
              '@type': schemaType,
              name: location.displayName || location.cityName,
              url:
                type === 'metro'
                  ? `${baseUrl}/metro/${location.name}`
                  : `${baseUrl}/city/${location.cityName}-${location.stateNameShort}`,
            },
    })),
  };
};

export const combineSchemas = (...schemas: any[]) => schemas.filter(Boolean);
